import { Text } from '@mantine/core';
import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { SortingState } from '@tanstack/react-table';
import {
	createFacetedMinMaxValues,
	createFacetedRowModel,
	createFacetedUniqueValues,
	createFilteredRowModel,
	createPaginatedRowModel,
	createSortedRowModel,
	useTable,
} from '@tanstack/react-table';

import { AddIcon } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useCurrentLocation } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { ToolbarButton } from '@apple/ui/data-table/controls/Toolbar/toolbar-button';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { TableLayout } from '@apple/ui/layouts/TableLayout';
import { Link } from '@apple/ui/link/Link';
import { FormattedDate } from '@apple/utils/globalization/components/FormattedDate';

import { getAllAnnouncements } from '@apple/features/announcements/api/management';
import type { Announcement } from '@apple/features/announcements/models/management';
import { requireAuth } from '@apple/features/auth/utils/require-auth';

const features = createDataTableFeatures({
	filteredRowModel: createFilteredRowModel(),
	sortedRowModel: createSortedRowModel(),
	paginatedRowModel: createPaginatedRowModel(),
	facetedRowModel: createFacetedRowModel(),
	facetedUniqueValues: createFacetedUniqueValues(),
	facetedMinMaxValues: createFacetedMinMaxValues(),
});

export const Route = createFileRoute('/_authed/_admin/content/announcements')({
	component: AnnouncementsManagePageRoute,
	wrapInSuspense: true,
	beforeLoad: args => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.ContentManagement',
		});

		return {
			announcementsQueryOptions: queryOptions({
				queryKey: ['announcements'],
				queryFn: () => getAllAnnouncements(),
				placeholderData: keepPreviousData,
				throwOnError: true,
				initialData: [],
			}),
		};
	},
	loader: ({ context: { queryClient, announcementsQueryOptions } }) => {
		void queryClient.prefetchQuery(announcementsQueryOptions);
	},
});

const defaultFilters: { name?: string } = {};
const defaultSorting: SortingState = [{ id: 'createdDate', desc: true }];

function AnnouncementsManagePageRoute() {
	const { t } = useTranslation('content');
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const location = useCurrentLocation();
	const { announcementsQueryOptions } = Route.useRouteContext();
	const announcementsQuery = useQuery(announcementsQueryOptions);

	const tableState = useTableState<Announcement, { name?: string }>({
		search,
		navigate,
		defaultFilters,
		defaultSorting,
		fieldMap: [],
	});

	const table = useTable({
		features,
		data: announcementsQuery.data,
		...tableState,
		enableFilters: true,
		enableColumnFilters: true,
		enableSorting: true,
		columnResizeMode: 'onChange',
		columns: [
			{
				accessorKey: 'name',
				header: t('announcements.headers.name'),
				cell: ({ row }) => (
					<Link
						to='/content/announcements/$announcementId'
						state={{ from: location }}
						params={{ announcementId: row.original.contentGroupId! }}
						children={row.original.name}
					/>
				),
				filter: {
					group: t('announcements.filter-groups.details.title'),
				},
				size: 500,
			},
			{
				accessorKey: 'active',
				header: t('announcements.headers.status'),
				enableColumnFilter: false,
				cell: ({ row }) => {
					const status = row.original.active
						? t('announcements.labels.active')
						: t('announcements.labels.inactive');
					return <Text>{status}</Text>;
				},
				size: 100,
			},
			{
				accessorKey: 'createdDate',
				header: t('announcements.headers.createdDate'),
				enableColumnFilter: false,
				cell: ({ row }) => <FormattedDate value={row.original.createdDate} humanize />,
				size: 100,
			},
		],
	});

	return (
		<TableLayout
			title={t('announcements.title')}
			table={table}
			toolbarButtons={[
				<ToolbarButton
					key='add'
					tooltip={t('announcements.buttons.addAnnouncement')}
					icon={AddIcon}
					onClick={() =>
						void navigate({
							to: '/content/announcements/add',
							state: { from: location },
							viewTransition: false,
						})
					}
				/>,
			]}
		>
			<DataTable table={table} loading={announcementsQuery.isFetching} />
		</TableLayout>
	);
}
